import React, { ChangeEvent } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Field, Input, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { Controller, FieldValues, UseFormMethods } from 'react-hook-form';
import ICompanyFinancialInfo from '../../../../utils/types/ICompanyFinancialInfo';
import MonthDayPicker from '../../../common/formFields/monthDayPicker/MonthDayPicker';
import CurrencyCodeDropdown from '../../../common/formFields/CurrencyCodeDropdown';
import MsReportingSalesLocationDropDown from '../../../companyCreation/details/MsReportingSalesLocationDropDown';

interface ICompanyFinancialInfoFormFormProps {
  form: UseFormMethods<FieldValues>;
  data: ICompanyFinancialInfo;
}

const CompanyFinancialInfoForm = ({
  form,
  data,
}: ICompanyFinancialInfoFormFormProps): JSX.Element => {
  const { errors, control, setValue } = form;

  const onMsReportingSalesLocationChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const selectedValue = event.target.value;
    const selectedText = event.target.options[event.target.selectedIndex]?.text;
    const value = { id: selectedValue, name: selectedText };
    setValue('salesLocationForReporting', value);
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <MonthDayPicker
          form={form}
          dueDateDay={data?.statutoryYearEndDay}
          dueDateMonth={data?.statutoryYearEndMonth}
          dayFormName="statutoryYearEndDay"
          monthFormName="statutoryYearEndMonth"
          label="Statutory Year End"
          required
        />
        <Controller
          name="financialType"
          control={control}
          defaultValue={data?.financialType || ''}
          render={({ onChange, name }): React.ReactElement => (
            <Field label="Financial Type" validationMessage={errors?.financialType?.message}>
              <Input
                id="financialType"
                name={name}
                value={data?.financialType || ''}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                }}
                readOnly
                disabled
              />
            </Field>
          )}
        />
        <Controller
          name="statutoryCurrency"
          control={control}
          defaultValue={data?.statutoryCurrency}
          rules={{
            required: {
              value: true,
              message: 'Please enter a Statutory Reporting Currency',
            },
          }}
          render={(): React.ReactElement => (
            <CurrencyCodeDropdown
              label="Statutory Reporting Currency"
              propertyName="statutoryCurrency"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('statutoryCurrency', newValue);
              }}
              defaultValue={data?.statutoryCurrency}
              errorMessage={errors?.statutoryCurrency?.message}
              width={500}
              required
            />
          )}
        />
        <Controller
          name="functionalCurrency"
          control={control}
          defaultValue={data?.functionalCurrency}
          rules={{
            required: {
              value: true,
              message: 'Please enter a Functional Currency',
            },
          }}
          render={(): React.ReactElement => (
            <CurrencyCodeDropdown
              label="Functional Currency"
              propertyName="functionalCurrency"
              handleChange={(newValue: string, valueKey: string): void => {
                setValue('functionalCurrency', newValue);
              }}
              defaultValue={data?.functionalCurrency}
              errorMessage={errors?.functionalCurrency?.message}
              width={500}
              required
            />
          )}
        />
        <Controller
          name="salesLocationForReporting"
          control={control}
          defaultValue={data?.salesLocationForReporting}
          render={(): JSX.Element => {
            return (
              <MsReportingSalesLocationDropDown
                defaultValue={data?.salesLocationForReporting?.id}
                onChange={onMsReportingSalesLocationChange}
              />
            );
          }}
        />
        <Controller
          name="taxJurisdictionCode"
          control={control}
          defaultValue={data?.taxJurisdictionCode || ''}
          render={({ onChange, value }): React.ReactElement => (
            <Field
              label="Tax Jurisdiction Code"
              required
              validationMessage={errors?.taxJurisdictionCode?.message}
            >
              <Input
                id="taxJurisdictionCode"
                name="taxJurisdictionCode"
                value={value}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                }}
              />
            </Field>
          )}
          rules={{
            required: {
              value: true,
              message: 'Please enter a Tax Jurisdiction Code',
            },
            maxLength: {
              value: 100,
              message: 'Tax Jurisdiction Code cannot have more than 100 characters',
            },
          }}
        />
      </Stack>
    </FluentProvider>
  );
};

export default CompanyFinancialInfoForm;
